/**
 * Pytania na rodzinną rozmowę o pieniądzach.
 *
 * Filozofia: Sianko nie uczy o pieniądzach. Inicjuje rozmowę między
 * rodzicem a dzieckiem — to oni są ekspertami od własnych wartości.
 * Pytania są otwarte, niesugerujące, bez "dobrej" odpowiedzi.
 *
 * Pojawiają się raz w piątek po recap'ie. Brak streaków, brak shame
 * ("w zeszłym tygodniu nie pogadaliście" nigdy nie istnieje), brak
 * przypomnień. "OK, pogadamy" zapisuje odbytą rozmowę w Firestore
 * (rodzinnie, nie per urządzenie) — roczna liczba rozmów pojawia się
 * w podsumowaniach jako docenienie, nigdy jako presja.
 *
 * Dobór pytania jest deterministyczny per tydzień (cała rodzina widzi
 * to samo) i sekwencyjny po temacie (THEME_ORDER) — kolejne tygodnie
 * idą przez program: świadomość → wartość → cierpliwość → praca →
 * posiadanie → dawanie → relacje → offline → marzenia, zapętlone.
 * Pula jest filtrowana po wieku najmłodszego dziecka (member.birthYear):
 * pytania "6-8" nie trafiają do nastolatków, a "12+" do sześciolatków.
 */

/** Temat programowy — wyznacza kolejność w sekwencji tygodni. */
export type MoneyTalkTheme =
  | "swiadomosc"
  | "wartosc"
  | "cierpliwosc"
  | "praca"
  | "posiadanie"
  | "dawanie"
  | "relacje"
  | "offline"
  | "marzenia";

const THEME_ORDER: MoneyTalkTheme[] = [
  "swiadomosc",
  "wartosc",
  "cierpliwosc",
  "praca",
  "posiadanie",
  "dawanie",
  "relacje",
  "offline",
  "marzenia",
];

/**
 * Pasmo wiekowe pytania. Dobór po NAJMŁODSZYM dziecku przy stole:
 * rozmowa jest wspólna, więc pytanie musi być w zasięgu najmłodszego.
 */
export type MoneyTalkAge = "all" | "6-8" | "9-11" | "12+";

export type MoneyTalkPrompt = {
  /** Główne pytanie — krótkie, otwarte, w 2 osobie. */
  question: string;
  /** Wskazówka dla rodzica — przypomnienie postawy, nie scenariusz. */
  hint: string;
  theme: MoneyTalkTheme;
  age: MoneyTalkAge;
};

export const MONEY_TALK_PROMPTS: MoneyTalkPrompt[] = [
  // ─── Wartość, wybór, refleksja ────────────────────────────────────
  {
    question: "Na co najchętniej teraz odkładasz — i dlaczego akurat to?",
    hint: "Słuchaj odpowiedzi. Nie oceniaj wyboru.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Co najfajniej kupiłaś/eś za swoje pieniądze w tym roku?",
    hint: "Wspomnienie często mówi więcej niż sama rzecz.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Czy znasz kogoś, kto ma za dużo rzeczy? Jak to wygląda?",
    hint: "Pytanie o nadmiar — bez wskazywania palcem.",
    theme: "wartosc",
    age: "all",
  },
  {
    question:
      "Gdybyś musiał/a oddać 10 zł komuś kto ma mniej niż Ty — komu i dlaczego?",
    hint: "O dawaniu, nie o obowiązku.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Czego z tego co masz nie kupiłabyś/byś znowu? Czemu?",
    hint: "Refleksja po fakcie — nie wstyd, tylko nauka.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Czy są rzeczy, które chciałabyś/byś ale wiesz że ich nie kupisz?",
    hint: "O różnicy między pragnieniem a planem.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Co chciałabyś/byś, żeby tata/mama wiedział/a o twoim odkładaniu?",
    hint: "Zaproszenie do podzielenia się tym co ważne.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Czym różni się fajne od potrzebne? Dasz przykład każdego?",
    hint: "Bez sugerowania definicji — niech znajdzie własne.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Czy łatwiej Ci poczekać tydzień, czy wydać od razu? Czemu?",
    hint: "O cierpliwości — bez moralizowania.",
    theme: "wartosc",
    age: "all",
  },
  {
    question:
      "Gdybyś dostał/a 100 zł na coś dla całej rodziny — co byś wybrał/a?",
    hint: "O myśleniu o innych — naturalnie, nie wymuszone.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Co Cię ostatnio zaskoczyło że tyle kosztuje? Albo że tak mało?",
    hint: "O świadomości cen — bez korekty.",
    theme: "wartosc",
    age: "all",
  },
  {
    question:
      "Czy jest coś, czego nie kupiłeś/aś, a żałujesz że tego nie zrobiłeś/aś?",
    hint: "O nieoczywistym koszcie braku decyzji.",
    theme: "wartosc",
    age: "all",
  },
  {
    question: "Za co można płacić, a czego nie da się kupić?",
    hint: "Przytulanie, sen, śmiech. Niech samo odkryje.",
    theme: "wartosc",
    age: "6-8",
  },

  // ─── Pieniądze — wartość, świadomość, decyzja ─────────────────────
  {
    question: "Co byś zrobił/a, gdybyś znalazł/a 50 zł na ulicy?",
    hint: "Bez podpowiedzi „dobrej” odpowiedzi — sam test moralny jest cenniejszy niż wynik.",
    theme: "swiadomosc",
    age: "all",
  },
  {
    question: "Czy są rzeczy, na które tata/mama wydaje pieniądze, a Ty byś nie?",
    hint: "Pierwszy raz, kiedy dziecko ocenia priorytety dorosłych. Wytrzymaj to.",
    theme: "swiadomosc",
    age: "all",
  },
  {
    question:
      "Wolałbyś/abyś dostawać kieszonkowe co tydzień, czy raz w miesiącu? Czemu?",
    hint: "Pokaże poziom dojrzałości w planowaniu — bez testu.",
    theme: "swiadomosc",
    age: "all",
  },
  {
    question: "Jak myślisz, ile kosztuje miesięcznie nasz dom?",
    hint: "Niech zgaduje. Potem powiedz prawdę — ten moment zostaje na lata.",
    theme: "swiadomosc",
    age: "9-11",
  },
  {
    question: "Czy pieniądze mogą zepsuć przyjaźń?",
    hint: "Otwarte. Sprawdza, czy dziecko miało już taką sytuację.",
    theme: "swiadomosc",
    age: "9-11",
  },
  {
    question: "Co jest droższe niż myślałeś/aś, a co tańsze?",
    hint: "O odkryciu, że ceny nie odpowiadają intuicji wartości.",
    theme: "swiadomosc",
    age: "all",
  },
  {
    question:
      "Gdybyś miał/a wybrać: prezent za 200 zł teraz, czy 400 zł za rok — co i dlaczego?",
    hint: "Klasyczny test odraczania gratyfikacji. Nie ma złej odpowiedzi.",
    theme: "swiadomosc",
    age: "9-11",
  },
  {
    question: "Czy znasz kogoś, kto ma mało pieniędzy ale wydaje się szczęśliwy?",
    hint: "Bez ciągnięcia w morał — sama obserwacja wystarczy.",
    theme: "swiadomosc",
    age: "all",
  },
  {
    question: "Gdybyś miał/a 10 zł, co byś z nimi zrobił/a?",
    hint: "Proste i konkretne. Nie podpowiadaj kategorii.",
    theme: "swiadomosc",
    age: "6-8",
  },
  {
    question: "Skąd się biorą pieniądze, które mamy w domu?",
    hint: "Niech opowie własną teorię. Prostuj delikatnie, bez wykładu.",
    theme: "swiadomosc",
    age: "6-8",
  },
  {
    question: "Która rzecz w Twoim pokoju była najdroższa? Zgadniesz?",
    hint: "Zabawa w zgadywanie cen — bez sprawdzianu.",
    theme: "swiadomosc",
    age: "6-8",
  },
  {
    question:
      "Ile kosztują miesięcznie wszystkie subskrypcje w naszym domu? Strzelaj.",
    hint: "Policzcie razem. Wynik zwykle zaskakuje obie strony.",
    theme: "swiadomosc",
    age: "12+",
  },
  {
    question: "Po czym poznajesz, że reklama próbuje Tobą manipulować?",
    hint: "Niech poda przykład z dzisiaj — z telefonu, nie z teorii.",
    theme: "swiadomosc",
    age: "12+",
  },
  {
    question:
      "Czy influencer, który poleca produkt, mówi prawdę? Po czym to poznać?",
    hint: "O odróżnianiu szczerego polecenia od płatnej współpracy.",
    theme: "swiadomosc",
    age: "12+",
  },
  {
    question:
      "Do czego służy konto w banku? Co się dzieje z pieniędzmi, które tam leżą?",
    hint: "Jeśli sam/a nie wiesz wszystkiego — sprawdźcie razem. To też lekcja.",
    theme: "swiadomosc",
    age: "12+",
  },

  // ─── Odraczanie gratyfikacji, planowanie, cierpliwość ─────────────
  {
    question: "Co takiego zrobiłeś/aś w tym tygodniu, co było trudne ale opłaciło się?",
    hint: "Buduje narrację „wytrwałość → rezultat” bez moralizowania.",
    theme: "cierpliwosc",
    age: "all",
  },
  {
    question: "Czy jest coś, na co czekasz dłużej niż miesiąc? Jak to znosisz?",
    hint: "Ucz świadomości czekania jako kompetencji.",
    theme: "cierpliwosc",
    age: "all",
  },
  {
    question: "Gdybyś miał/a swój własny dzień bez żadnego planu — co byś robił/a?",
    hint: "Test, czy dziecko potrafi nudę. Często odsłania prawdziwe pasje.",
    theme: "cierpliwosc",
    age: "all",
  },
  {
    question: "Co byś chciał/a umieć za rok, czego nie umiesz dziś?",
    hint: "Cel rozwojowy — nie zakupowy. Zapamiętaj odpowiedź na potem.",
    theme: "cierpliwosc",
    age: "all",
  },
  {
    question: "Co najtrudniejszego udało Ci się ostatnio dokończyć?",
    hint: "Skup się na ukończeniu, nie na talencie.",
    theme: "cierpliwosc",
    age: "all",
  },
  {
    question: "Czy jest coś, co zacząłeś/aś i nie skończył/a? Co byś z tym zrobił/a teraz?",
    hint: "Bez wstydu — refleksja o tym, że porzucanie też jest decyzją.",
    theme: "cierpliwosc",
    age: "all",
  },
  {
    question:
      "Co jest lepsze: duża paczka cukierków dziś, czy mała codziennie przez tydzień?",
    hint: "Odraczanie w wersji do dotknięcia. Każda odpowiedź jest dobra.",
    theme: "cierpliwosc",
    age: "6-8",
  },
  {
    question:
      "Co wolisz: dostać zabawkę w prezencie, czy samemu uzbierać na nią do skarbonki?",
    hint: "Pierwsza rozmowa o dumie z uzbierania.",
    theme: "cierpliwosc",
    age: "6-8",
  },

  // ─── Dawanie, wspólnota, rodzeństwo ───────────────────────────────
  {
    question: "Jaką najlepszą rzecz zrobił dla Ciebie ktoś z rodziny w tym tygodniu?",
    hint: "Trening wdzięczności — bez tabelek i punktów.",
    theme: "dawanie",
    age: "all",
  },
  {
    question:
      "Co takiego umie Twoje rodzeństwo (lub kuzyn/ka), czego Ty byś chciał/a się nauczyć?",
    hint: "Przeformułowuje rywalizację w podziw.",
    theme: "dawanie",
    age: "all",
  },
  {
    question:
      "Gdybyś musiał/a wybrać jedno wspólne rodzinne wspomnienie z tego roku — które?",
    hint: "Pokazuje co naprawdę zostaje (rzadko: prezenty).",
    theme: "dawanie",
    age: "all",
  },
  {
    question: "Co byś chciał/a robić częściej z mamą/tatą, a co rzadziej?",
    hint: "Wytrzymaj „rzadziej”. To informacja, nie atak.",
    theme: "dawanie",
    age: "all",
  },
  {
    question: "Czy jest ktoś w klasie, kogo nikt nie zaprasza? Co byś z tym zrobił/a?",
    hint: "Bez nacisku na działanie — sama refleksja jest pracą.",
    theme: "dawanie",
    age: "all",
  },
  {
    question: "Co takiego dał Ci ktoś, czego nie kupisz w sklepie?",
    hint: "Czas, uwaga, nauczenie czegoś. Niech znajdzie sam.",
    theme: "dawanie",
    age: "all",
  },
  {
    question:
      "Co byś zrobił/a, gdyby kolega chciał pożyczyć Twoją ulubioną zabawkę?",
    hint: "O dzieleniu się i granicach — jedno i drugie jest w porządku.",
    theme: "dawanie",
    age: "6-8",
  },

  // ─── Praca, wysiłek, sprawczość ───────────────────────────────────
  {
    question:
      "Co takiego zrobiłeś/aś sam/a, z czego jesteś dumny/a, a nikt o tym nie wie?",
    hint: "Wewnętrzna duma > zewnętrzne pochwały.",
    theme: "praca",
    age: "all",
  },
  {
    question: "Gdybyś mógł/a mieć dorosłą pracę na jeden dzień — co byś wybrał/a?",
    hint: "Odpowiedź mówi więcej o ciekawości niż o ambicji.",
    theme: "praca",
    age: "all",
  },
  {
    question:
      "Czy jest coś, co robisz w domu, a co powinno być Twoim zdaniem płatne?",
    hint: "Bez negocjacji — zbieraj dane, nie reaguj.",
    theme: "praca",
    age: "all",
  },
  {
    question: "Co Cię najbardziej irytuje w obowiązkach domowych? Da się to zmienić?",
    hint: "Pokazuje, że frustracja może być początkiem ulepszenia, nie buntu.",
    theme: "praca",
    age: "all",
  },
  {
    question: "Co jest fajniejsze: dostać coś gotowego, czy zrobić coś samemu?",
    hint: "Pytanie podchwytliwe. Nie ma poprawnej odpowiedzi.",
    theme: "praca",
    age: "all",
  },
  {
    question: "Co takiego umiesz, czego inne dzieci w Twoim wieku raczej nie?",
    hint: "Buduje tożsamość przez kompetencje, nie posiadanie.",
    theme: "praca",
    age: "all",
  },
  {
    question:
      "Gdybyś mógł/a zarobić pierwsze własne pieniądze poza domem — jak?",
    hint: "O pomysłach, nie o pozwoleniach. Notuj, wrócisz do tego.",
    theme: "praca",
    age: "12+",
  },
  {
    question:
      "Ile godzin pracy kosztuje rzecz za 100 zł przy stawce 30 zł za godzinę? Czy to zmienia, jak na nią patrzysz?",
    hint: "Przeliczanie ceny na czas — najpraktyczniejsze narzędzie tej rozmowy.",
    theme: "praca",
    age: "12+",
  },

  // ─── Aktywność poza ekranem, nuda, uwaga ──────────────────────────
  {
    question: "Jaką najfajniejszą rzecz wymyśliłeś/aś ostatnio, kiedy się nudziłeś/aś?",
    hint: "Łączy nudę z kreatywnością — nie z karą.",
    theme: "offline",
    age: "all",
  },
  {
    question: "Co byś robił/a, gdybyś nie miał/a telefonu/tabletu przez cały weekend?",
    hint: "Hipotetyczne — nie groźba. Niech sam się zastanowi.",
    theme: "offline",
    age: "all",
  },
  {
    question:
      "Kiedy ostatnio leżałeś/aś i patrzyłeś/aś w sufit? Co tam się działo w głowie?",
    hint: "Normalizuje sieć domyślną. Bez ironii.",
    theme: "offline",
    age: "all",
  },
  {
    question: "Co Cię ostatnio zachwyciło na zewnątrz — drzewo, kamień, owad, cokolwiek?",
    hint: "Trening uważności — nie geografia.",
    theme: "offline",
    age: "all",
  },
  {
    question: "Czy są rzeczy, które robisz w aplikacji, a żałujesz potem?",
    hint: "Bez przesłuchania. Otwarcie tematu = wygrana.",
    theme: "offline",
    age: "all",
  },

  // ─── Posiadanie, rzeczy, nadmiar ──────────────────────────────────
  {
    question: "Co byś zabrał/a, gdybyś musiał/a spakować całe życie do jednej walizki?",
    hint: "Pokazuje hierarchię wartości — bez moralizowania o minimalizmie.",
    theme: "posiadanie",
    age: "all",
  },
  {
    question:
      "Czy jest w Twoim pokoju coś, czego nie używałeś/aś od miesięcy? Komu by się przydało?",
    hint: "Most do oddawania, bez nakazu.",
    theme: "posiadanie",
    age: "all",
  },
  {
    question: "Co byś zrobił/a, gdyby Twój ulubiony przedmiot się zepsuł?",
    hint: "Sprawdza, czy dziecko myśli o naprawie czy tylko o wymianie.",
    theme: "posiadanie",
    age: "all",
  },
  {
    question: "Pamiętasz prezent, którego bardzo chciałeś/aś, a potem zapomniałeś/aś o nim?",
    hint: "Bez triumfu „a nie mówiłem”. Tylko obserwacja.",
    theme: "posiadanie",
    age: "all",
  },
  {
    question: "Co masz, czego nikt inny w klasie nie ma? Czy to coś znaczy?",
    hint: "Nie chodzi o rzeczy luksusowe — może to być książka po dziadku.",
    theme: "posiadanie",
    age: "all",
  },
  {
    question:
      "Czy zdarzyło Ci się kupić coś przez internet pod wpływem chwili? I jak było potem?",
    hint: "Bez kazania o impulsywności — własna historia rodzica pomaga bardziej.",
    theme: "posiadanie",
    age: "12+",
  },

  // ─── Marzenia, przyszłość, cele ───────────────────────────────────
  {
    question:
      "Co chciałbyś/abyś robić, kiedy będziesz dorosły/a — nawet gdyby nie płacili?",
    hint: "Odsiewa karierę od pasji.",
    theme: "marzenia",
    age: "all",
  },
  {
    question:
      "Gdybyś mógł/a zaprojektować idealny weekend, jak wyglądałby godzina po godzinie?",
    hint: "Trening planowania w przyjemnej wersji.",
    theme: "marzenia",
    age: "all",
  },
  {
    question: "Co takiego chciałbyś/abyś, żeby było inne w naszej rodzinie?",
    hint: "Daj prawo do krytyki. Notuj.",
    theme: "marzenia",
    age: "all",
  },
  {
    question:
      "Jakie zwierzę chciałbyś/abyś mieć, gdyby nie było żadnych ograniczeń — i dlaczego akurat to?",
    hint: "Mówi więcej o temperamencie niż o zoologii.",
    theme: "marzenia",
    age: "all",
  },
  {
    question: "Co byś zrobił/a, gdybyś wiedział/a, że nie możesz przegrać?",
    hint: "Klasyk, ale działa. Marzenia bez asekuracji.",
    theme: "marzenia",
    age: "all",
  },
  {
    question: "Gdybyś mógł/a żyć w jakimś innym czasie historycznym, kiedy i czemu?",
    hint: "Test wyobraźni — bez znajomości historii w odpowiedzi.",
    theme: "marzenia",
    age: "all",
  },
  {
    question: "Gdyby zwierzęta płaciły pieniędzmi, co kupowałby pies?",
    hint: "Absurd otwiera rozmowę lepiej niż powaga. Pośmiejcie się.",
    theme: "marzenia",
    age: "6-8",
  },

  // ─── Pieniądze + relacje ──────────────────────────────────────────
  {
    question: "Czy łatwiej byłoby Ci pożyczyć 20 zł komuś, czy poprosić o pożyczkę?",
    hint: "O psychologii długu — bez wkraczania w konkretne sytuacje.",
    theme: "relacje",
    age: "9-11",
  },
  {
    question: "Co byś czuł/a, gdyby kolega zapłacił za Ciebie w sklepiku?",
    hint: "Pierwsza rozmowa o relacji „pieniądze a poczucie winy”.",
    theme: "relacje",
    age: "9-11",
  },
  {
    question: "Czy są rzeczy, których nie kupiłbyś/abyś za żadne pieniądze?",
    hint: "Otwiera temat, że nie wszystko jest na sprzedaż.",
    theme: "relacje",
    age: "all",
  },
  {
    question:
      "Gdybyś dostał/a 1000 zł z warunkiem „nie możesz tego wydać na siebie” — co byś zrobił/a?",
    hint: "Twórcze ograniczenie. Nikt nie pyta dzieci o takie rzeczy.",
    theme: "relacje",
    age: "9-11",
  },
  {
    question: "Co byś poradził/a komuś rok młodszemu od Ciebie o oszczędzaniu?",
    hint: "Dziecko jako ekspert — naturalny boost pewności.",
    theme: "relacje",
    age: "all",
  },
  {
    question:
      "Kiedy ostatnio zmieniłeś/aś zdanie na temat tego, na co warto wydać pieniądze?",
    hint: "Promuje refleksyjność, nie konsekwencję za wszelką cenę.",
    theme: "relacje",
    age: "all",
  },
  {
    question: "Czy jest coś, co kupiłbyś/abyś tylko dlatego, że inni mają?",
    hint: "Bez przesłuchania — czysta świadomość presji rówieśniczej.",
    theme: "relacje",
    age: "all",
  },
  {
    question:
      "Wolałbyś/abyś dostać 100 zł, czy spędzić cały dzień robiąc coś z rodzicem?",
    hint: "Co kilka miesięcy odpowiedź zmienia się — to też ciekawe.",
    theme: "relacje",
    age: "all",
  },
  {
    question:
      "Co byś zrobił/a, gdyby znajomy namawiał Cię na grę, w której można wygrać pieniądze?",
    hint: "Pierwsza rozmowa o hazardzie. Spokojnie, bez paniki.",
    theme: "relacje",
    age: "12+",
  },
];

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Pula pytań dla rodziny — filtrowana po wieku najmłodszego dziecka,
 * posortowana w kolejności programowej (THEME_ORDER, stabilnie).
 * Brak wieku (birthYear nieustawiony) → pełna pula, zachowanie legacy.
 */
export function weekPromptPool(youngestAge?: number): MoneyTalkPrompt[] {
  const bands: MoneyTalkAge[] =
    youngestAge === undefined
      ? ["all", "6-8", "9-11", "12+"]
      : youngestAge <= 8
        ? ["all", "6-8"]
        : youngestAge <= 11
          ? ["all", "9-11"]
          : ["all", "9-11", "12+"];
  return MONEY_TALK_PROMPTS.filter((p) => bands.includes(p.age)).sort(
    (a, b) => THEME_ORDER.indexOf(a.theme) - THEME_ORDER.indexOf(b.theme),
  );
}

/** Numer tygodnia od epoki — rośnie o 1 co tydzień, niezależnie od roku. */
export function weekIndex(weekStart: Date): number {
  return Math.floor(weekStart.getTime() / WEEK_MS);
}

/**
 * Wybiera prompt na ten tydzień deterministycznie — wszyscy w rodzinie
 * tego samego tygodnia widzą to samo pytanie, a kolejne tygodnie idą
 * sekwencyjnie przez program tematyczny (nie losowo).
 */
export function pickWeekPrompt(
  weekStart: Date,
  youngestAge?: number,
): MoneyTalkPrompt {
  const pool = weekPromptPool(youngestAge);
  return pool[weekIndex(weekStart) % pool.length];
}
